import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, fmtOdds, tierRank } from './ui'
import { PickCard } from './cards'
import * as api from './api'

/**
 * Game drill-in — tap a game to see both probable goalies, the market context
 * (total + moneylines), and each team's top projected plays, every one tappable
 * into the player detail. Built from the slate game object + the all-markets
 * leaderboard (filtered to this game) — no new backend.
 */
export default function GameModal({ g, date, onPlayer, onClose }) {
  const [plays, setPlays] = useState(null)
  useEffect(() => {
    let alive = true
    api.getNHLLeaderboard('', date, 250).then(r => {
      if (!alive) return
      const rows = (r.plays || []).filter(p =>
        (g.game_id && p.game_id === g.game_id) || p.team === g.home || p.team === g.away)
      setPlays(rows)
    })
    return () => { alive = false }
  }, [g.game_id, date])

  // Rank by conviction (tier) first, prob only as the tie-break — otherwise the
  // naturally-higher-prob markets (shots/saves) always bury a genuine goals/assists
  // best bet.
  const teamTop = (team) => (plays || [])
    .filter(p => p.team === team)
    .sort((a, b) => tierRank(a.tier) - tierRank(b.tier) || (b.prob_over || 0) - (a.prob_over || 0))
    .slice(0, 5)

  const TeamBlock = ({ team, goalie }) => {
    const top = teamTop(team)
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontWeight: 800, fontSize: 14, color: T.text }}>{team}</span>
          <span style={{ fontSize: 11, color: T.muted }}>🥅 {goalie || 'TBD'}</span>
        </div>
        {plays === null ? <Spinner /> :
          top.length === 0 ? <div style={{ fontSize: 11.5, color: T.muted, fontStyle: 'italic', padding: '4px 0' }}>
            No projected plays yet.</div> :
          top.map((p, i) => <PickCard key={`${p.player_id}-${p.market}-${i}`} p={p} onClick={() => onPlayer(p)} />)}
      </div>
    )
  }

  const scored = g.home_score != null
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 300, background: '#000A',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.bg, width: '100%', maxWidth: 460,
        maxHeight: '92vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', border: `1px solid ${T.border}`,
        borderBottom: 'none', padding: '14px 14px 28px' }}>
        <div style={{ width: 38, height: 4, borderRadius: 3, background: T.border2, margin: '0 auto 12px' }} />

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, color: T.text }}>{g.away} @ {g.home}</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2, fontFamily: T.mono }}>
              {g.state}{scored ? ` · ${g.away_score}-${g.home_score}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: T.card2, border: `1px solid ${T.border}`,
            color: T.muted2, borderRadius: 8, width: 30, height: 30, fontSize: 15, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 12, color: T.muted2, flexWrap: 'wrap' }}>
          {g.total_line != null && <span>Total <b style={{ color: T.text }}>{g.total_line}</b></span>}
          {g.away_ml != null && <span>{g.away} <b style={{ color: T.text }}>{fmtOdds(g.away_ml)}</b></span>}
          {g.home_ml != null && <span>{g.home} <b style={{ color: T.text }}>{fmtOdds(g.home_ml)}</b></span>}
        </div>

        <TeamBlock team={g.away} goalie={g.away_goalie} />
        <TeamBlock team={g.home} goalie={g.home_goalie} />
      </div>
    </div>
  )
}
